import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Gallery from './components/Gallery';
import ImageDetail from './components/ImageDetail';
import Footer from './components/Footer';
import About from './components/About';
import Contact from './components/Contact';
import Categories from './components/Categories';
import Scholars from './components/Scholars';
import TopicView from './components/TopicView';
import FloatingContactButton from './components/FloatingContactButton';
import AdminPanel from './components/admin/AdminPanel';
import { getAllImages, getImageById, trackVisitor } from './firebase/dataService';

const _ap = '/' + atob('c2FsbWFuLWNwLTIwMjQ=');

function AppWithAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // تتبع الزوار الفريدين
    trackVisitor();

    const loadData = async () => {
      try {
        const images = await getAllImages();
        
        if (!images || images.length === 0) {
          throw new Error('لا توجد صور في قاعدة البيانات');
        }
        
        const response = await fetch('/data.json');
        const jsonData = await response.json();
        
        const visibleImages = images.filter(img => img.visible !== false);
        
        // ترتيب الصور حسب تاريخ الإضافة (الأحدث أولاً)
        const sortedImages = visibleImages.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA; // الأحدث أولاً
        });
        
        setData({
          profile: jsonData.profile,
          social_links: jsonData.social_links,
          images: sortedImages
        });
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        try {
          const response = await fetch('/data.json');
          const jsonData = await response.json();
          setData(jsonData);
        } catch (fallbackError) {
          console.error('Fallback also failed:', fallbackError);
          setData(null);
        }
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const MainSite = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const selectedCategory = 'الكل';


    if (loading) {
      return (
        <div className="loading-container">
          <div className="loader"></div>
          <p>جاري التحميل...</p>
        </div>
      );
    }

    if (!data) {
      return (
        <div className="error-container">
          <p>❌ حدث خطأ في تحميل البيانات</p>
        </div>
      );
    }

    return (
      <div className="App">
        <Helmet>
          <title>سلمان بن عبدالله الحران</title>
          <meta name="description" content="موقع الفقير إلى الله : سلمان بن عبدالله الحران - فوائد علمية وشرعية في الفقه والعقيدة والحديث والتفسير وغيرها" />
          <meta property="og:title" content="سلمان بن عبدالله الحران" />
          <meta property="og:description" content="موقع الفقير إلى الله : سلمان بن عبدالله الحران - فوائد علمية وشرعية في الفقه والعقيدة والحديث والتفسير وغيرها" />
          <meta property="og:url" content="https://salman-alkhatep.com/" />
        </Helmet>
        <Header profile={data.profile} />
        {data.images && data.images.length > 0 && (
          <Gallery
            images={data.images}
            profile={data.profile}
            onImageClick={(image, category) => {
              navigate(`/post/${image.id}`, { state: { category: category || 'الكل' } });
            }}
            initialCategory={selectedCategory}
          />
        )}
        <Footer profile={data.profile} />
        <FloatingContactButton />
      </div>
    );
  };

  const ImageDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [image, setImage] = useState(null);
    const [allImages, setAllImages] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);
    const activeCategory = location.state?.category || 'الكل';

    useEffect(() => {
      const load = async () => {
        try {
          const [img, images] = await Promise.all([
            getImageById(id),
            getAllImages()
          ]);
          const visible = images
            .filter(i => i.visible !== false)
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          setImage(img);
          setAllImages(visible);
        } catch {
          setImage(null);
        }
        setPageLoading(false);
      };
      load();
    }, [id]);

    if (pageLoading) {
      return (
        <div className="loading-container">
          <div className="loader"></div>
          <p>جاري التحميل...</p>
        </div>
      );
    }

    if (!image) return <Navigate to="/" replace />;

    const filteredImages = activeCategory === 'الكل'
      ? allImages
      : allImages.filter(i => i.category === activeCategory);

    const currentIndex = filteredImages.findIndex(i => i.id === image.id);

    const pageTitle = `${image.caption} - سلمان بن عبدالله الحران`;
    const pageDesc = image.extractedText
      ? image.extractedText.slice(0, 160)
      : image.caption || 'موقع الفقير إلى الله : سلمان بن عبدالله الحران - فوائد علمية وشرعية في الفقه والعقيدة والحديث والتفسير وغيرها';
    const pageUrl = `https://salman-alkhatep.com/post/${image.id}`;

    return (
      <div className="App">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDesc} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDesc} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:image" content={image.url} />
          <meta property="twitter:title" content={pageTitle} />
          <meta property="twitter:description" content={pageDesc} />
          <meta property="twitter:url" content={pageUrl} />
          <meta property="twitter:image" content={image.url} />
        </Helmet>
        <ImageDetail
          image={image}
          images={filteredImages}
          currentIndex={currentIndex}
          onBack={() => navigate('/')}
          onImageUpdated={(updated) => setImage(updated)}
          onNavigate={(newImage) => navigate(`/post/${newImage.id}`, { state: { category: activeCategory } })}
        />
      </div>
    );
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainSite />} />
        <Route path="/about" element={
          <>
            <Header profile={data?.profile} />
            <About />
            <Footer profile={data?.profile} />
            <FloatingContactButton />
          </>
        } />
        <Route path="/contact" element={
          <>
            <Header profile={data?.profile} />
            <Contact />
            <Footer profile={data?.profile} />
            <FloatingContactButton />
          </>
        } />
        <Route path="/categories" element={
          <>
            <Header profile={data?.profile} />
            <Categories />
            <Footer profile={data?.profile} />
            <FloatingContactButton />
          </>
        } />
        <Route path="/scholars" element={
          <>
            <Header profile={data?.profile} />
            <Scholars />
            <Footer profile={data?.profile} />
            <FloatingContactButton />
          </>
        } />
        <Route path="/scholars/topic/:topicId" element={
          <>
            <Header profile={data?.profile} />
            <TopicView />
            <Footer profile={data?.profile} />
            <FloatingContactButton />
          </>
        } />
        <Route path="/post/:id" element={<ImageDetailPage />} />
        <Route path={_ap} element={<AdminPanel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default AppWithAdmin;
